import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { MarketDepthTemplate as template } from './market-depth.template';
import { MarketDepthStyles as styles } from './market-depth.styles';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../services/enum.service';
import { IStructuredChatService } from '../../services/structured-chat.service';
import { MarketDepthVppaGrid } from '../../components/page-specific/market-depth/market-depth-vppa-grid/market-depth-vppa-grid';
import { CancelModal } from '../../components/modals/cancel-modal/cancel-modal';
import { IRfq, IVppa } from '../../types';
import { IRfqService, IVppaService } from '../../services';
import { logger } from '@genesislcap/foundation-comms';
import { RfqModal } from '../../components/modals/rfq-modal/rfq-modal';
import { VppaModal } from '../../components/modals/vppa-modal/vppa-modal';
import { METHOD_EXECUTION as METHOD_EXECUTION_ENUM } from '../../utils/enums/generic.enum';
import { notifyError } from '../../utils/notify';
import { IMarketDepthService } from '../../services/market-depth.service';
import { IAuthService } from '../../services/auth.service';
const name = 'market-depth';

export enum MarketDepthField {
  ISO = 'ISO',
  TECHNOLOGY = 'TECHNOLOGY',
  PRODUCT_TYPE = 'PRODUCT_TYPE',
  METHOD_EXECUTION = 'METHOD_EXECUTION',
  APPLICABLE_REC_PROGRAM = 'APPLICABLE_REC_PROGRAM',
  PRICE_MIN = 'FIXED_PRICE',
  PRICE_MAX = 'FIXED_PRICE',
  CONTRACT_TYPE = 'CONTRACT_TYPE',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  SETTLEMENT_INDEX = 'SETTLEMENT_INDEX',
  TENOR_START_DATE = 'TENOR_START_DATE',
  PROJECT_OPERATIONAL_STATUS = 'PROJECT_OPERATIONAL_STATUS',
  STATUS = 'STATUS',
}

export enum MarketDepthOperation {
  BIGGER = '>',
  SMALLER = '<',
  EQUAL = '==',
  NOT_EQUAL = '!=',
  GREATER_OR_EQUAL = '>=',
  LOWER_OR_EQUAL = '<=',
}
export enum MarketDepthTable {
  VPPA = 'VPPA',
  RFQ = 'RFQ',
  BOTH = 'BOTH',
}

export enum MarketDepthCriteriaType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
}

export interface ICriteriaObject {
  FIELD: MarketDepthField;
  VALUE: String | Number;
  OPERATION: MarketDepthOperation;
  TABLE: MarketDepthTable;
  TYPE: MarketDepthCriteriaType;
}

@customElement({
  name,
  template,
  styles,
})
export class MarketDepth extends FASTElement {
  @IEnumService enumService: IEnumService;
  @IStructuredChatService structuredChatService: IStructuredChatService;
  @IMarketDepthService marketDepthService: IMarketDepthService;
  @IVppaService vppaService: IVppaService;
  @IRfqService rfqService: IRfqService;
  @IAuthService authService: IAuthService;

  @observable marketDepthVppaGridElement: MarketDepthVppaGrid;
  @observable enums: Array<EnumTranslate> = [];
  @observable contractTypes: Array<EnumTranslate> = [];
  @observable technologyTypes: Array<EnumTranslate> = [];

  @observable isVppaSelected: boolean = false;
  @observable isVPPA: boolean = true;
  @observable rfq: any;
  @observable methodOfExecution: METHOD_EXECUTION_ENUM = null;
  public vppaModal: VppaModal;
  public rfqModal: RfqModal;
  public cancelModal: CancelModal;

  connectedCallback(): void {
    super.connectedCallback();

    this.loadEnums();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.enums = [];
    this.contractTypes = [];
  }

  private async loadEnums() {
    await Promise.all([
      this.enumService.getFilteredEnumValues(CASCADE_ENUM.CONTRACT_TYPE),
      this.enumService.getEnumValues(CASCADE_ENUM.TECHNOLOGY),
    ]).then(([contractType, technology]) => {
      if (contractType.REPLY) {
        this.contractTypes = contractType.REPLY;
        this.enums.push(...contractType.REPLY);
      }

      if (technology.REPLY) {
        this.technologyTypes = technology.REPLY;
        this.enums.push(...technology.REPLY);
      }
    });
  }

  removeFilter(index: number) {
    if (!index || index < 0) return;
    this.marketDepthService.criteriaList = this.marketDepthService.criteriaList?.filter(
      (x, c) => c !== index
    );
  }

  public getStructuredChat(data) {
    if (data.VPPA_ID)
      this.structuredChatService.openStructuredChat({
        selectedVPPAIDs: [data.VPPA_ID],
        selectedRFQIDs: [],
        selectedNegotiationID: '',
      });
  }

  public getVppaList(data) {
    if (data.RFQ_ID) {
      this.rfq = data;
    }
  }

  public cancelVppaSelection() {
    this.rfq = null;
    this.isVppaSelected = false;
  }

  public sendNegotiations() {
    const selectedRows = this.marketDepthVppaGridElement.getSelectedRows();
    const vppaIds = [];
    selectedRows.forEach((elemnt) => vppaIds.push(elemnt.VPPA_ID));
    this.structuredChatService.openStructuredChat({
      selectedVPPAIDs: vppaIds,
      selectedRFQIDs: [this.rfq.RFQ_ID],
      selectedNegotiationID: '',
    });
  }

  public vppaSelected(isVppaSelected) {
    this.isVppaSelected = isVppaSelected;
  }

  public cancelVppa(data: IVppa) {
    this.vppaService.eventVppaCancel(data.VPPA_ID, data.CONTRACT_TYPE).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.cancelModal.closeDialog();
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      }
    );
  }

  public cancelRfq(data: IRfq) {
    this.rfqService.eventRfqCancel(data.RFQ_ID, data.CONTRACT_TYPE).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.cancelModal.closeDialog();
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      }
    );
  }

  public deleteVppa(data) {
    this.isVPPA = true;
    this.cancelModal.openModal(
      data,
      'VPPA',
      'Are you sure you wish to cancel ' + data.VPPA_TICKER + '?'
    );
  }

  public deleteRfq(data) {
    this.isVPPA = false;
    this.cancelModal.openModal(
      data,
      'RFQ',
      'Are you sure you wish to cancel ' + data.RFQ_TICKER + '?'
    );
  }

  public editRfq(data) {
    this.rfqModal.openModal(true, data.RFQ_ID, data.RFQ_TICKER);
  }

  public openRfqModal() {
    this.rfqModal.openModal(false, null);
  }

  public editVppa(data) {
    this.vppaModal.openModal(
      true,
      data.VPPA_ID,
      data.VPPA_COMPANY_ID,
      data.VPPA_TICKER
    );
  }

  public methodOfExecutionUpdated(methodOfExecution: METHOD_EXECUTION_ENUM) {
    if (methodOfExecution) {
      this.methodOfExecution = Object.values(METHOD_EXECUTION_ENUM)?.find(
        (x) => x === methodOfExecution
      );
    } else {
      this.methodOfExecution = null;
    }
  }
}

export enum MarketDepthLabel {
  ISO = 'ISO',
  APPLICABLE_REC_PROGRAM = 'APPLICABLE_REC_PROGRAM',
  TECHNOLOGY = 'Technology Type',
  FIXED_PRICE = 'Price',
  CONTRACT_TYPE = 'Contract Type',
  SETTLEMENT_POINT = 'Settlement Point',
  SETTLEMENT_INDEX = 'Settlement Index',
  TENOR_START_DATE = 'Tenor Date',
}
